import { useQuery } from '@apollo/client';
import useDialog from 'hooks/useDialog';
import useSiteMutations from 'FeatureModules/Site/hooks/useSiteMutations';
import { PLOTS_PER_PAGE } from 'utils/pagination.utils';
import { GET_PLOTS_WITH_FARMS } from 'graphql/secure/queries/plot.queries';
import { GET_SITE_OVERVIEW } from 'graphql/secure/queries/site.queries';
import { IPlotsFilters } from 'graphql/secure/queries/types/plot-query.types';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { IActivity } from 'FeatureModules/Activity/types/activity.types';
import { IBaseDocument } from 'FeatureModules/Document/types/document.types';
import { IPlot } from 'FeatureModules/Site/types/plot.types';
import { ESiteType, IBaseSite, ISite } from 'FeatureModules/Site/types/site.types';
import { IGraphQlConnection } from 'types/core.types';
import { removeGraphConnections } from 'utils/graphql.utils';

interface IUseSiteProps {
  // Can be used to skip the useQueryParam search and use the internal search term instead
  skipPlotsQuery?: boolean;
}

const useSite = ({ skipPlotsQuery }: IUseSiteProps = {}) => {
  const { id } = useParams();
  const { openDialog } = useDialog();
  const { deleteSite } = useSiteMutations();

  // The cluster
  const { data, loading, error } = useQuery<{
    site: ISite;
    activities: IGraphQlConnection<IActivity>;
    documents: IGraphQlConnection<IBaseDocument>;
    sharedDocuments: IGraphQlConnection<IBaseDocument>;
  }>(GET_SITE_OVERVIEW, {
    variables: { id },
  });
  const {
    data: plotsData,
    loading: loadingPlots,
    error: plotError,
    fetchMore,
  } = useQuery<{ plots: IGraphQlConnection<IPlot> }, { filters: IPlotsFilters; first: number }>(
    GET_PLOTS_WITH_FARMS,
    {
      variables: {
        filters:
          // it can never happen since the query gets skipped
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          data?.site?.siteType === ESiteType.CLUSTER ? { siteClusterId: id! } : { farmIds: [id!] },
        first: PLOTS_PER_PAGE,
      },
      skip:
        !id ||
        !data?.site ||
        (data?.site?.siteType !== ESiteType.CLUSTER && data?.site?.siteType !== ESiteType.FARM) ||
        skipPlotsQuery,
      notifyOnNetworkStatusChange: true,
    }
  );

  const hasClusterMorePlots: boolean = !!plotsData?.plots?.pageInfo?.hasNextPage;
  const totalPlotsCount: number = plotsData?.plots?.count || 0;

  const site = data?.site;
  const siteActivities = data?.activities ? removeGraphConnections(data.activities) : [];
  const internalSiteDocuments = data?.documents ? removeGraphConnections(data.documents) : [];
  const sharedSiteDocuments = data?.sharedDocuments
    ? removeGraphConnections(data.sharedDocuments)
    : [];

  const totalSiteActivitiesCount: number = data?.activities?.count || 0;
  const totalInternalSiteDocumentsCount: number = data?.documents?.count || 0;
  const totalSharedSiteDocumentsCount: number = data?.sharedDocuments?.count || 0;

  const plots: IPlot[] = useMemo(
    () => (plotsData?.plots ? removeGraphConnections(plotsData.plots) : []),
    [plotsData]
  );

  const handleLoadMorePlots = () => {
    if (plotsData?.plots.pageInfo?.endCursor) {
      fetchMore({
        variables: {
          after: plotsData?.plots.pageInfo?.endCursor,
        },
      });
    }
  };

  const handleEditSite = (siteArg?: IBaseSite) => {
    const siteItem = siteArg || site;

    if (siteItem?.siteType === ESiteType.CLUSTER) {
      return openDialog({
        type: 'ADD_EDIT_SITE_CLUSTER',
        props: {
          siteCluster: siteItem,
        },
      });
    }

    openDialog({
      type: 'ADD_EDIT_SITE',
      props: {
        site: siteItem,
      },
    });
  };

  const handleEditFarmPlots = (site: IBaseSite) => {
    if (site.siteType !== ESiteType.FARM) {
      throw new Error('This dialog can opened because the site is not a farm');
    }
    openDialog({
      type: 'ADD_EDIT_FARM_PLOTS',
      props: {
        farm: site,
      },
    });
  };

  const handleDeleteSite = (siteArg?: IBaseSite) => {
    const siteItem = siteArg || site;
    if (!siteItem) return;

    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete site',
        text: 'Are you sure you want to delete this site? All data will be lost and you will not be able to recover this item.',
        submitText: 'Delete',
        itemTitle: siteItem.title,
        displayCloseButton: true,
        onSubmit: () => {
          deleteSite({ variables: { id: siteItem.id } });
        },
        onCancel: () => undefined,
      },
    });
  };

  const handleCreateSiteDocument = () => {
    if (!id) {
      return;
    }
    openDialog({
      type: 'UPLOAD_DOCUMENTS',
      props: {
        defaultSiteId: id,
        // Preselect owner of the document if the site has an owner
        defaultOwnedById: site?.ownedBy?.id,
      },
    });
  };

  const handleAddActivity = () => {
    openDialog({
      type: 'ADD_EDIT_ACTIVITY',
      props: { mode: 'add-activity', site },
    });
  };

  const handleEditActivity = (activity: IActivity) => {
    openDialog({
      type: 'ADD_EDIT_ACTIVITY',
      props: {
        mode: 'edit-activity',
        activity: {
          ...activity,
        },
      },
    });
  };

  const handleCreateSiteDocumentRequest = () => {
    openDialog({
      type: 'PARTNER_REQUEST',
      props: {
        requestType: 'DOCUMENT',
      },
    });
  };

  return {
    site,
    loading,
    loadingPlots,
    error: error || plotError,
    hasClusterMorePlots,
    plots,
    internalSiteDocuments,
    siteActivities,
    totalSiteActivitiesCount,
    totalInternalSiteDocumentsCount,
    sharedSiteDocuments,
    totalSharedSiteDocumentsCount,
    totalPlotsCount,
    handleLoadMorePlots,
    handleEditFarmPlots,
    handleEditSite,
    handleDeleteSite,
    handleCreateSiteDocument,
    handleAddActivity,
    handleEditActivity,
    handleCreateSiteDocumentRequest,
  };
};

export default useSite;
